import { useCallback, useEffect, useRef, useState, PointerEvent as ReactPointerEvent } from 'react';
import { ClipboardList, X, Minus, Loader2, Check, AlertCircle, GripHorizontal } from 'lucide-react';

// Modelo BANT pré-preenchido em leads sem anotação. Só é gravado quando o usuário edita.
const NOTES_TEMPLATE = 'B - \nA - \nN - \nT - \n\nInformações adicionais:\n';

const DEFAULT_SIZE = { w: 460, h: 460 };
const MIN_SIZE = { w: 300, h: 280 };
import { useServiceNotes, ServiceNotesLead } from '../../contexts/ServiceNotesContext';
import { useGlobalChat } from '../../contexts/GlobalChatContext';
import { fetchLeadServiceNotes, updateLeadServiceNotes } from '../../services/leadsService';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const AUTOSAVE_MS = 800;

function ServiceNotesWindow({ lead, onClose, onFocus }: { lead: ServiceNotesLead; onClose: () => void; onFocus: () => void }) {
  // Arrastar: a janela nasce ancorada no canto e o arrasto só soma um deslocamento
  // (translate), limitado à viewport pra o cabeçalho nunca sair da tela.
  const windowRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ px: number; py: number; ox: number; oy: number; rect: DOMRect } | null>(null);

  const startDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    onFocus();
    if ((e.target as HTMLElement).closest('button')) return;
    const el = windowRef.current;
    if (!el) return;
    dragRef.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y, rect: el.getBoundingClientRect() };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const moveDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = Math.min(Math.max(e.clientX - d.px, -d.rect.left), window.innerWidth - d.rect.right);
    const dy = Math.min(Math.max(e.clientY - d.py, -d.rect.top), window.innerHeight - d.rect.bottom);
    setOffset({ x: d.ox + dx, y: d.oy + dy });
  };

  const endDrag = () => { dragRef.current = null; };

  // Redimensionar: a janela é ancorada embaixo à direita, então a alça fica no canto
  // superior esquerdo e arrastá-la pra cima/esquerda aumenta a janela.
  const [size, setSize] = useState(DEFAULT_SIZE);
  const resizeRef = useRef<{ px: number; py: number; w: number; h: number } | null>(null);

  const startResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onFocus();
    resizeRef.current = { px: e.clientX, py: e.clientY, w: size.w, h: size.h };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const moveResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = resizeRef.current;
    if (!r) return;
    const maxW = window.innerWidth - 32;
    const maxH = window.innerHeight - 32;
    setSize({
      w: Math.min(Math.max(r.w - (e.clientX - r.px), MIN_SIZE.w), maxW),
      h: Math.min(Math.max(r.h - (e.clientY - r.py), MIN_SIZE.h), maxH),
    });
  };

  const endResize = () => { resizeRef.current = null; };

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [templateApplied, setTemplateApplied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [minimized, setMinimized] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Último texto digitado e o último persistido, em refs pra o flush ao fechar
  // enxergar o valor atual sem depender do ciclo de render.
  const textRef = useRef('');
  const savedTextRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const savingRef = useRef(false);

  const save = useCallback(async () => {
    if (savingRef.current || textRef.current === savedTextRef.current) return;
    savingRef.current = true;
    const toSave = textRef.current;
    setSaveState('saving');
    try {
      await updateLeadServiceNotes(lead.id, toSave);
      savedTextRef.current = toSave;
      setSavedAt(new Date());
      setSaveState(textRef.current === toSave ? 'saved' : 'dirty');
    } catch (err) {
      console.error('Erro ao salvar anotações de atendimento:', err);
      setSaveState('error');
    } finally {
      savingRef.current = false;
    }
    // Digitou durante o salvamento: salva de novo.
    if (textRef.current !== savedTextRef.current && !timerRef.current) {
      timerRef.current = setTimeout(() => { timerRef.current = undefined; save(); }, AUTOSAVE_MS);
    }
  }, [lead.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await fetchLeadServiceNotes(lead.id);
        if (cancelled) return;
        // Lead sem anotação: mostra o modelo, mas o "salvo" continua sendo o vazio —
        // assim abrir a janela e fechar sem digitar não grava só o modelo no banco.
        const initial = current.trim() === '' ? NOTES_TEMPLATE : current;
        textRef.current = initial;
        savedTextRef.current = initial === NOTES_TEMPLATE ? NOTES_TEMPLATE : current;
        setText(initial);
        setTemplateApplied(initial === NOTES_TEMPLATE);
      } catch (err) {
        console.error('Erro ao carregar anotações de atendimento:', err);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead.id]);

  // Com o modelo aplicado, o cursor já começa na primeira linha, depois de "B - ".
  useEffect(() => {
    if (!loading && templateApplied && textareaRef.current) {
      textareaRef.current.setSelectionRange(4, 4);
    }
  }, [loading, templateApplied]);

  // Ao fechar a janela (ou sair do sistema) o que ainda não foi salvo é enviado.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (textRef.current !== savedTextRef.current) {
      updateLeadServiceNotes(lead.id, textRef.current).catch(err => console.error('Erro ao salvar anotações ao fechar:', err));
    }
  }, [lead.id]);

  const handleChange = (value: string) => {
    setText(value);
    textRef.current = value;
    setSaveState('dirty');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { timerRef.current = undefined; save(); }, AUTOSAVE_MS);
  };

  const status = {
    idle: null,
    dirty: <span className="text-gray-500">Editando…</span>,
    saving: <span className="text-gray-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Salvando…</span>,
    saved: <span className="text-emerald-700 dark:text-emerald-400 flex items-center gap-1"><Check size={12} /> Salvo{savedAt ? ` às ${savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}</span>,
    error: (
      <button onClick={() => save()} className="text-red-700 dark:text-red-400 flex items-center gap-1 hover:underline">
        <AlertCircle size={12} /> Erro ao salvar — tentar de novo
      </button>
    ),
  }[saveState];

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="flex items-center gap-2 pl-3 pr-2 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg shadow-amber-500/30 transition-all max-w-[220px]"
        title={`Anotações de atendimento — ${lead.name}`}
      >
        <ClipboardList size={18} className="shrink-0" />
        <span className="text-xs font-bold truncate">{lead.name || 'Sem nome'}</span>
        <span
          role="button"
          aria-label="Fechar anotações"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="p-1 hover:bg-white/20 rounded-full"
        >
          <X size={14} />
        </span>
      </button>
    );
  }

  return (
    <div
      ref={windowRef}
      onPointerDown={onFocus}
      style={{ transform: `translate(${offset.x}px, ${offset.y}px)`, width: size.w, height: size.h }}
      className="relative max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] bg-white dark:bg-slate-900 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.18)] flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-200"
    >
      <div
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        title="Arraste para mover"
        className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/40 cursor-grab active:cursor-grabbing touch-none select-none"
      >
        <GripHorizontal size={14} className="text-amber-400 shrink-0" />
        <ClipboardList size={16} className="text-amber-700 dark:text-amber-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 leading-none">Anotações de atendimento</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-0.5" title={lead.name}>{lead.name || 'Sem nome'}</p>
        </div>
        <button onClick={() => setMinimized(true)} aria-label="Minimizar" className="p-1.5 text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg">
          <Minus size={16} />
        </button>
        <button onClick={onClose} aria-label="Fechar anotações" className="p-1.5 text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg">
          <X size={16} />
        </button>
      </div>

      <div
        onPointerDown={startResize}
        onPointerMove={moveResize}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        title="Arraste para redimensionar"
        className="absolute top-0 left-0 z-10 w-4 h-4 cursor-nwse-resize touch-none"
      >
        <span className="absolute top-1 left-1 w-2 h-2 border-t-2 border-l-2 border-amber-400/80 rounded-tl-sm" />
      </div>

      <div className="p-3 flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500"><Loader2 className="animate-spin" size={20} /></div>
        ) : loadError ? (
          <div className="flex-1 flex items-center justify-center text-sm text-red-500 text-center px-4">Não foi possível carregar as anotações deste lead.</div>
        ) : (
          <textarea
            ref={textareaRef}
            autoFocus
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Anote aqui o que aconteceu no atendimento, combinados, objeções, próximos passos…"
            className="w-full flex-1 min-h-0 p-3 text-sm bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 outline-none resize-none text-gray-800 dark:text-gray-100 placeholder:text-gray-400"
          />
        )}
        <div className="h-5 mt-1.5 text-[11px] flex items-center justify-between">
          <span>{status}</span>
          <span className="text-gray-500">salva automaticamente</span>
        </div>
      </div>
    </div>
  );
}

// Janelas não-modais: ficam por cima do sistema sem bloquear a tela, então o usuário
// segue usando o CRM enquanto anota. Se o chat flutuante estiver aberto, as anotações
// ficam ao lado dele pra não sobrepor.
export function FloatingServiceNotes() {
  const { openLeads, closeServiceNotes } = useServiceNotes();
  const { activeChatLead } = useGlobalChat();
  const [topId, setTopId] = useState<number | null>(null);

  if (openLeads.length === 0) return null;

  return (
    <div className={`fixed bottom-4 z-[70] flex flex-row-reverse items-end gap-3 pointer-events-none ${activeChatLead ? 'right-4 lg:right-[452px]' : 'right-4'}`}>
      {openLeads.map(lead => (
        <div key={lead.id} className="pointer-events-auto relative" style={{ zIndex: topId === lead.id ? 2 : 1 }}>
          <ServiceNotesWindow lead={lead} onClose={() => closeServiceNotes(lead.id)} onFocus={() => setTopId(lead.id)} />
        </div>
      ))}
    </div>
  );
}
